import React, { useEffect } from 'react';
import { sanitizeText } from '@flow-studio/flow-observer-core';
import type { ExecutionStatusData, ExecutionEventInfo } from '@flow-studio/flow-observer-core';
import { useLocalStorage } from '../../hooks/useLocalStorage.js';
import { WebJsonField, fmtKey } from '../shared/WebJsonField';
import { Icons } from 'process-pipelines-common';
import './DetailPane.css';

export interface DetailPaneProps {
  data?: ExecutionStatusData;
  eventInfo?: ExecutionEventInfo;
  rawDetail?: Record<string, unknown>;
  onClose?: () => void;
}

type ViewMode = 'formatted' | 'json';
type DetailTab = 'overview' | 'nodeResult' | 'configChanges' | 'resolvedConfig' | 'metadata';

export const DetailPane: React.FC<DetailPaneProps> = ({ data, eventInfo, rawDetail, onClose }) => {
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>('detail-pane-view-mode', 'formatted');
  const [detailTab, setDetailTab] = useLocalStorage<DetailTab>('detail-pane-tab', 'overview');
  const [outputModal, setOutputModal] = React.useState<{ title: string; data: any } | null>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [jsonCopied, setJsonCopied] = React.useState(false);

  useEffect(() => {
    // Show toast alert when any action has ActionType === "HIL"
    const hasHilAction = eventInfo?.eventDescription?.actionType?.actions?.some(
      (action) => action?.actionType?.toUpperCase() === 'HIL'
    );

    if (hasHilAction) {
      showToast('HIL Action Detected', 'This is a Hardware-In-the-Loop action');
    }
  }, [eventInfo?.eventDescription?.actionType?.actions]);

  // Handle tab validation when eventInfo changes
  useEffect(() => {
    if (!eventInfo) return;

    // Check if current tab is valid for this event
    const hasNodeResult = (eventInfo?.eventDescription?.metadata as any)?.nodeResult;
    const hasChanges = (eventInfo?.eventDescription?.metadata as any)?.changes;
    const hasResolvedConfig = (eventInfo?.eventDescription?.metadata as any)?.resolvedConfiguration;
    const hasMetadata = (eventInfo?.eventDescription?.metadata as any)?.outputData || (eventInfo?.eventDescription?.metadata as any)?.inputData;

    const isCurrentTabValid =
      detailTab === 'overview' ||
      (detailTab === 'nodeResult' && hasNodeResult) ||
      (detailTab === 'configChanges' && hasChanges) ||
      (detailTab === 'resolvedConfig' && hasResolvedConfig) ||
      (detailTab === 'metadata' && hasMetadata);

    // If current tab is not valid, find the first available tab
    if (!isCurrentTabValid) {
      if (hasNodeResult) {
        setDetailTab('nodeResult');
      } else if (hasChanges) {
        setDetailTab('configChanges');
      } else if (hasResolvedConfig) {
        setDetailTab('resolvedConfig');
      } else if (hasMetadata) {
        setDetailTab('metadata');
      } else {
        setDetailTab('overview');
      }
    }
  }, [eventInfo, detailTab]);

  if (!data && !eventInfo) {
    return (
      <div className="detail-pane detail-pane--empty">
        <button className="detail-pane__close" onClick={onClose} aria-label="Close">×</button>
        <div className="detail-pane__empty-state">
          <span>Select a log entry to view details</span>
        </div>
      </div>
    );
  }

  return (
    <div className="detail-pane" style={isFullscreen ? { position: 'fixed', inset: 0, zIndex: 10000 } : undefined}>
      <div className="detail-pane__header">
        <div className="detail-pane__title">
          Stage/SubStage/Description
        </div>
        <div className="detail-pane__controls">
          <div className="detail-pane__view-toggle">
            <button
              className={`detail-pane__view-btn ${viewMode === 'formatted' ? 'active' : ''}`}
              onClick={() => setViewMode('formatted')}
            >
              Formatted
            </button>
            <button
              className={`detail-pane__view-btn ${viewMode === 'json' ? 'active' : ''}`}
              onClick={() => setViewMode('json')}
            >
              JSON
            </button>
          </div>
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            style={{
              background: 'none',
              border: 'none',
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: '16px',
              padding: '4px 8px',
              marginRight: '4px',
            }}
          >
            {isFullscreen ? '⊙' : '⛶'}
          </button>
          <button className="detail-pane__close" onClick={onClose} aria-label="Close">×</button>
        </div>
      </div>

      <div className="detail-pane__content" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {viewMode === 'formatted' && (
          <div className="detail-pane__formatted" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
            {/* Sub-tab navigation */}
            <div style={{ display: 'flex', gap: '0px', borderBottom: '1px solid rgba(100,116,139,0.2)', flexShrink: 0, background: 'rgba(15,23,42,0.4)', overflow: 'auto', height: '28px', width: '100%' }}>
              <button
                onClick={() => setDetailTab('overview')}
                style={{
                  padding: '4px 12px',
                  fontSize: '11px',
                  fontWeight: detailTab === 'overview' ? '600' : '500',
                  color: detailTab === 'overview' ? '#22c55e' : '#9ca3af',
                  background: 'none',
                  border: 'none',
                  borderBottom: detailTab === 'overview' ? '2px solid #22c55e' : 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.15s',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Overview
              </button>
              {(eventInfo?.eventDescription?.metadata as any)?.nodeResult && (
                <button
                  onClick={() => setDetailTab('nodeResult')}
                  style={{
                    padding: '4px 12px',
                    fontSize: '11px',
                    fontWeight: detailTab === 'nodeResult' ? '600' : '500',
                    color: detailTab === 'nodeResult' ? '#22c55e' : '#9ca3af',
                    background: 'none',
                    border: 'none',
                    borderBottom: detailTab === 'nodeResult' ? '2px solid #22c55e' : 'none',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'color 0.15s',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Node Result
                </button>
              )}
              {(eventInfo?.eventDescription?.metadata as any)?.changes && (
                <button
                  onClick={() => setDetailTab('configChanges')}
                  style={{
                    padding: '4px 12px',
                    fontSize: '11px',
                    fontWeight: detailTab === 'configChanges' ? '600' : '500',
                    color: detailTab === 'configChanges' ? '#22c55e' : '#9ca3af',
                    background: 'none',
                    border: 'none',
                    borderBottom: detailTab === 'configChanges' ? '2px solid #22c55e' : 'none',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'color 0.15s',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Changes
                </button>
              )}
              {(eventInfo?.eventDescription?.metadata as any)?.resolvedConfiguration && (
                <button
                  onClick={() => setDetailTab('resolvedConfig')}
                  style={{
                    padding: '4px 12px',
                    fontSize: '11px',
                    fontWeight: detailTab === 'resolvedConfig' ? '600' : '500',
                    color: detailTab === 'resolvedConfig' ? '#22c55e' : '#9ca3af',
                    background: 'none',
                    border: 'none',
                    borderBottom: detailTab === 'resolvedConfig' ? '2px solid #22c55e' : 'none',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'color 0.15s',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Config
                </button>
              )}
              {((eventInfo?.eventDescription?.metadata as any)?.outputData || (eventInfo?.eventDescription?.metadata as any)?.inputData) && (
                <button
                  onClick={() => setDetailTab('metadata')}
                  style={{
                    padding: '4px 12px',
                    fontSize: '11px',
                    fontWeight: detailTab === 'metadata' ? '600' : '500',
                    color: detailTab === 'metadata' ? '#22c55e' : '#9ca3af',
                    background: 'none',
                    border: 'none',
                    borderBottom: detailTab === 'metadata' ? '2px solid #22c55e' : 'none',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'color 0.15s',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Metadata
                </button>
              )}
            </div>

            {/* Tab Content */}
            {detailTab === 'overview' && (
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {data && (
              <div className="detail-pane__box detail-pane__data-box">
                <div className="detail-pane__box-title">Data</div>
                <div className="detail-pane__property">
                  <span className="detail-pane__label">Status</span>
                  <span className={`detail-pane__badge detail-pane__badge--${data.runState}`}>
                    {data.runState.toUpperCase()}
                  </span>
                </div>
                {data.currentNodeLabel && (
                  <div className="detail-pane__property">
                    <span className="detail-pane__label">Current Node</span>
                    <span className="detail-pane__value">{sanitizeText(data.currentNodeLabel)}</span>
                  </div>
                )}
                {data.elapsedMs !== undefined && (
                  <div className="detail-pane__property">
                    <span className="detail-pane__label">Duration</span>
                    <span className="detail-pane__value">{formatMs(data.elapsedMs)}</span>
                  </div>
                )}
                {data.nodesTotal > 0 && (
                  <>
                    <div className="detail-pane__property">
                      <span className="detail-pane__label">Nodes Completed</span>
                      <span className="detail-pane__value">{data.nodesCompleted}/{data.nodesTotal}</span>
                    </div>
                    {data.nodesFailed > 0 && (
                      <div className="detail-pane__property">
                        <span className="detail-pane__label detail-pane__label--error">Failed</span>
                        <span className="detail-pane__value detail-pane__value--error">{data.nodesFailed}</span>
                      </div>
                    )}
                  </>
                )}
                {data.errorMessage && (
                  <div className="detail-pane__property">
                    <span className="detail-pane__label detail-pane__label--error">Error</span>
                    <span className="detail-pane__value detail-pane__value--error">{sanitizeText(data.errorMessage)}</span>
                  </div>
                )}
                {data.startedAt && (
                  <div className="detail-pane__property">
                    <span className="detail-pane__label">Started</span>
                    <span className="detail-pane__value">{formatIsoDate(data.startedAt)}</span>
                  </div>
                )}
                {data.completedAt && (
                  <div className="detail-pane__property">
                    <span className="detail-pane__label">Completed</span>
                    <span className="detail-pane__value">{formatIsoDate(data.completedAt)}</span>
                  </div>
                )}
              </div>
            )}

            {eventInfo && (
              <div className="detail-pane__box detail-pane__event-box">
                <div className="detail-pane__box-title">Event</div>
                {(rawDetail?.item as any)?.id && (
                  <div className="detail-pane__property">
                    <span className="detail-pane__label">Item ID</span>
                    <span className="detail-pane__value">{(rawDetail?.item as any)?.id}</span>
                  </div>
                )}
                <div className="detail-pane__property">
                  <span className="detail-pane__label">Type</span>
                  <span className="detail-pane__value">{eventInfo.eventType?.eventType || 'Unknown'}</span>
                </div>
                <div className="detail-pane__property">
                  <span className="detail-pane__label">Name</span>
                  <span className="detail-pane__value">{sanitizeText(eventInfo.eventType?.eventName || 'Unknown')}</span>
                </div>
                <div className="detail-pane__property">
                  <span className="detail-pane__label">Description</span>
                  <span className="detail-pane__value">{sanitizeText(eventInfo.eventType?.eventDescription || 'N/A')}</span>
                </div>
                <div className="detail-pane__property">
                  <span className="detail-pane__label">SubType</span>
                  <span className="detail-pane__value">{eventInfo.eventSubType || 'Unknown'}</span>
                </div>
                {eventInfo.eventDescription?.stage && (
                  <div className="detail-pane__property">
                    <span className="detail-pane__label">Stage</span>
                    <span className="detail-pane__value">{sanitizeText(eventInfo.eventDescription.stage)}</span>
                  </div>
                )}
                {eventInfo.eventDescription?.subStage && (
                  <div className="detail-pane__property">
                    <span className="detail-pane__label">Sub Stage</span>
                    <span className="detail-pane__value">{sanitizeText(eventInfo.eventDescription.subStage)}</span>
                  </div>
                )}
                {eventInfo.eventDescription?.actionType?.actions && eventInfo.eventDescription.actionType.actions.length > 0 && (
                  <>
                    {eventInfo.eventDescription.actionType.actions.map((action, idx) => (
                      <div key={idx}>
                        <div className="detail-pane__property">
                          <span className="detail-pane__label">Action Name</span>
                          <span className="detail-pane__value">{sanitizeText(action?.actionName || 'N/A')}</span>
                        </div>
                        <div className="detail-pane__property">
                          <span className="detail-pane__label">Action Type</span>
                          <span
                            className={`detail-pane__value ${
                              action?.actionType?.toUpperCase() === 'HIL'
                                ? 'detail-pane__value--warn'
                                : ''
                            }`}
                          >
                            {action?.actionType || 'Unknown'}
                          </span>
                        </div>
                        {action?.actionUi && action.actionUi.length > 0 && (
                          <div className="detail-pane__property">
                            <span className="detail-pane__label">UI Type</span>
                            <span className="detail-pane__value">
                              {action.actionUi.map((ui) => ui?.uiType || 'unknown').join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
                {eventInfo.cancellationInfo?.isCancellationRequested && (
                  <div className="detail-pane__property">
                    <span className="detail-pane__label detail-pane__label--warn">Cancellation</span>
                    <span className="detail-pane__badge detail-pane__badge--cancelled">REQUESTED</span>
                  </div>
                )}
                {eventInfo.error && (
                  <div className="detail-pane__property">
                    <span className="detail-pane__label detail-pane__label--error">Error</span>
                    <span className="detail-pane__value detail-pane__value--error">
                      {sanitizeText(eventInfo.error?.message || 'Unknown error')}
                    </span>
                  </div>
                )}
              </div>
            )}

            {Boolean(rawDetail?.item) && (
              <div className="detail-pane__box">
                <div className="detail-pane__box-title">Node</div>
                <WebJsonField value={rawDetail!.item} />
              </div>
            )}

            {Boolean(rawDetail?.status) && (
              <div className="detail-pane__box">
                <div className="detail-pane__box-title">Status</div>
                <WebJsonField value={rawDetail!.status} />
              </div>
            )}

            {Boolean(rawDetail?.progress) && (
              <div className="detail-pane__box">
                <div className="detail-pane__box-title">Progress</div>
                <WebJsonField value={rawDetail!.progress} />
              </div>
            )}

            {Boolean(rawDetail?.error) && (
              <div className="detail-pane__box">
                <div className="detail-pane__box-title detail-pane__box-title--error">Error</div>
                <WebJsonField value={rawDetail!.error} />
              </div>
            )}

            </div>
            )}

            {/* Tab Content - Node Result */}
            {detailTab === 'nodeResult' && eventInfo?.eventDescription?.metadata && (eventInfo.eventDescription.metadata as any)?.nodeResult && (
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div className="detail-pane__box">
                  <div className="detail-pane__box-title">
                    Node Result
                    <button
                      onClick={() => setOutputModal({
                        title: 'Node Result — ' + (rawDetail?.item as any)?.name,
                        data: (eventInfo.eventDescription?.metadata as any)?.nodeResult?.outputData || (eventInfo.eventDescription?.metadata as any)?.nodeResult
                      })}
                      style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        fontSize: '10px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                      }}
                    >
                      Result
                    </button>
                  </div>
                  <DynamicFormRenderer data={(eventInfo.eventDescription.metadata as any).nodeResult} />
                </div>
              </div>
            )}

            {/* Tab Content - Config Changes */}
            {detailTab === 'configChanges' && eventInfo?.eventDescription?.metadata && (eventInfo.eventDescription.metadata as any)?.changes && (
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div className="detail-pane__box">
                  <div className="detail-pane__box-title">Config Changes ({(eventInfo.eventDescription.metadata as any).changedCount})</div>
                  <ConfigChangesDisplay changes={(eventInfo.eventDescription.metadata as any).changes} />
                </div>
              </div>
            )}

            {/* Tab Content - Resolved Configuration */}
            {detailTab === 'resolvedConfig' && eventInfo?.eventDescription?.metadata && (eventInfo.eventDescription.metadata as any)?.resolvedConfiguration && (
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div className="detail-pane__box">
                  <div className="detail-pane__box-title">Resolved Configuration</div>
                  <ResolvedConfigDisplay config={(eventInfo.eventDescription.metadata as any).resolvedConfiguration} />
                </div>
              </div>
            )}

            {/* Tab Content - Metadata */}
            {detailTab === 'metadata' && eventInfo?.eventDescription?.metadata && ((eventInfo.eventDescription.metadata as any)?.outputData || (eventInfo.eventDescription.metadata as any)?.inputData) && (
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div className="detail-pane__box">
                  <div className="detail-pane__box-title">Metadata</div>
                  {(eventInfo.eventDescription.metadata as any)?.outputData && (
                    <div style={{ marginBottom: '16px' }}>
                      <DynamicFormRenderer data={(eventInfo.eventDescription.metadata as any).outputData} title="Output Data" />
                    </div>
                  )}
                  {(eventInfo.eventDescription.metadata as any)?.inputData && (
                    <div>
                      <DynamicFormRenderer data={(eventInfo.eventDescription.metadata as any).inputData} title="Input Data" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {viewMode === 'json' && (
          <div className="detail-pane__json-container">
            <div className="detail-pane__json-header">
              <button
                className="detail-pane__json-copy"
                onClick={() => {
                  const jsonText = JSON.stringify({ data, eventInfo, rawDetail }, null, 2);
                  navigator.clipboard.writeText(jsonText).then(() => {
                    setJsonCopied(true);
                    setTimeout(() => setJsonCopied(false), 1500);
                  });
                }}
                title="Copy JSON to clipboard"
              >
                {jsonCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="detail-pane__json">
              <pre>{JSON.stringify({ data, eventInfo, rawDetail }, null, 2)}</pre>
            </div>
          </div>
        )}
      </div>

      {/* Output Data Modal */}
      {outputModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setOutputModal(null)}
        >
          <div
            style={{
              background: '#1e293b',
              border: '1px solid rgba(100, 116, 139, 0.3)',
              borderRadius: '8px',
              width: '90vw',
              maxWidth: '1000px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid rgba(100,116,139,0.2)', flexShrink: 0 }}>
              <span style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 600 }}>{outputModal.title}</span>
              <button
                onClick={() => setOutputModal(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '0 8px',
                }}
              >
                ×
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
              <WebJsonField value={outputModal.data} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// fmtKey imported from shared/WebJsonField — replaces the old formatLabel

function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m ${s % 60}s`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

function formatIsoDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleString();
  } catch {
    return isoString;
  }
}

// Display config changes with original vs resolved comparison
const ConfigChangesDisplay: React.FC<{ changes: any[] }> = ({ changes }) => {
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set());

  const toggleExpanded = (key: string) => {
    const newSet = new Set(expandedKeys);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setExpandedKeys(newSet);
  };

  return (
    <div style={{ fontSize: '12px' }}>
      {changes.map((change, idx) => (
        <div
          key={idx}
          style={{
            marginBottom: '12px',
            borderLeft: '2px solid #4ade80',
            paddingLeft: '12px',
          }}
        >
          <button
            onClick={() => toggleExpanded(change.configKey)}
            style={{
              background: 'none',
              border: 'none',
              color: '#3b82f6',
              cursor: 'pointer',
              padding: 0,
              fontSize: '12px',
              fontWeight: '600',
              textAlign: 'left',
              width: '100%',
              marginBottom: '6px',
            }}
          >
            {expandedKeys.has(change.configKey) ? '▼' : '▶'} {change.configKey}
          </button>

          {expandedKeys.has(change.configKey) && (
            <>
              <div style={{ marginTop: '6px' }}>
                <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '4px' }}>Original</div>
                <pre
                  style={{
                    fontSize: '11px',
                    background: '#0f172a',
                    color: '#d1d5db',
                    padding: '8px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '250px',
                    margin: 0,
                    width: '100%',
                  }}
                >
                  {formatJsonString(change.original)}
                </pre>
              </div>
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '4px' }}>Resolved</div>
                <pre
                  style={{
                    fontSize: '11px',
                    background: '#0f172a',
                    color: '#d1d5db',
                    padding: '8px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '250px',
                    margin: 0,
                    width: '100%',
                  }}
                >
                  {formatJsonString(change.resolved)}
                </pre>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

// Helper to format JSON strings nicely
function formatJsonString(jsonStr: string): string {
  try {
    const parsed = JSON.parse(jsonStr);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return jsonStr;
  }
}

// Display node result summary with status and duration
const NodeResultDisplay: React.FC<{ nodeResult: any }> = ({ nodeResult }) => {
  return (
    <div style={{ fontSize: '12px', color: '#d1d5db' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div>
          <span style={{ color: '#9ca3af', fontSize: '10px' }}>Status</span>
          <div style={{ marginTop: '4px' }}>
            <span style={{
              padding: '2px 8px',
              borderRadius: '3px',
              background: nodeResult.isSuccess ? '#10b981' : '#ef4444',
              color: 'white',
              fontSize: '11px',
              fontWeight: '600',
            }}>
              {nodeResult.isSuccess ? <><Icons.CheckCircle /> Success</> : <><Icons.XCircle /> Failed</>}
            </span>
          </div>
        </div>
        <div>
          <span style={{ color: '#9ca3af', fontSize: '10px' }}>Duration</span>
          <div style={{ marginTop: '4px', color: '#86efac' }}>
            {nodeResult.durationMs}ms
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <span style={{ color: '#9ca3af', fontSize: '10px' }}>Output Port</span>
          <div style={{ marginTop: '4px', color: '#a5f3fc' }}>
            {nodeResult.outputPortKey}
          </div>
        </div>
        <div>
          <span style={{ color: '#9ca3af', fontSize: '10px' }}>Branches</span>
          <div style={{ marginTop: '4px', color: '#a5f3fc' }}>
            {Object.keys(nodeResult.outputBranches || {}).filter(k => nodeResult.outputBranches[k]).join(', ') || 'none'}
          </div>
        </div>
      </div>

      {nodeResult.errorMessage && (
        <div style={{ marginTop: '12px', padding: '8px', background: 'rgba(239, 68, 68, 0.1)', borderLeft: '2px solid #ef4444', color: '#fca5a5' }}>
          <span style={{ fontSize: '10px', color: '#9ca3af' }}>Error:</span>
          <div style={{ marginTop: '4px' }}>{nodeResult.errorMessage}</div>
        </div>
      )}

      {nodeResult.isSkipped && (
        <div style={{ marginTop: '8px', padding: '4px 8px', background: 'rgba(249, 115, 22, 0.1)', borderRadius: '3px', color: '#fb923c', fontSize: '11px' }}>
          ⊘ Skipped
        </div>
      )}

      {nodeResult.isRetry && (
        <div style={{ marginTop: '8px', padding: '4px 8px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '3px', color: '#60a5fa', fontSize: '11px' }}>
          ↻ Retry
        </div>
      )}
    </div>
  );
};

// Display resolved configuration with collapsible nested objects
const ResolvedConfigDisplay: React.FC<{ config: any }> = ({ config }) => {
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set());

  const toggleExpanded = (key: string) => {
    const newSet = new Set(expandedKeys);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setExpandedKeys(newSet);
  };

  const isComplexValue = (value: any): boolean => {
    return value !== null && (typeof value === 'object');
  };

  return (
    <div style={{ fontSize: '12px', color: '#d1d5db' }}>
      {Object.entries(config).map(([key, value]) => (
        <div key={key} style={{ marginBottom: '6px' }}>
          {isComplexValue(value) ? (
            <>
              <button
                onClick={() => toggleExpanded(key)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '11px',
                  fontWeight: '500',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                {expandedKeys.has(key) ? '▼' : '▶'} <span style={{ color: '#a78bfa' }}>{key}</span>: [object]
              </button>
              {expandedKeys.has(key) && (
                <pre
                  style={{
                    fontSize: '10px',
                    background: '#0f172a',
                    color: '#d1d5db',
                    padding: '8px',
                    borderRadius: '3px',
                    overflow: 'auto',
                    maxHeight: '150px',
                    marginTop: '4px',
                    marginBottom: '6px',
                    margin: '4px 0 6px 16px',
                  }}
                >
                  {JSON.stringify(value, null, 2)}
                </pre>
              )}
            </>
          ) : (
            <div style={{ marginLeft: '8px' }}>
              <span style={{ color: '#a78bfa' }}>{key}</span>
              <span style={{ color: '#6b7280' }}>: </span>
              <span style={{ color: '#86efac' }}>{String(value)}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Professional grid-based form renderer
const DynamicFormRenderer: React.FC<{ data: any; title?: string }> = ({ data, title }) => {
  const renderValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span style={{ color: '#9ca3af' }}>—</span>;
    }

    if (typeof value === 'boolean') {
      return (
        <span style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '4px',
          background: value ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          color: value ? '#10b981' : '#ef4444',
          fontWeight: '600',
          fontSize: '11px',
        }}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }

    if (typeof value === 'number') {
      return <span style={{ color: '#a5f3fc', fontFamily: 'monospace' }}>{value}</span>;
    }

    if (typeof value === 'string') {
      // Handle long strings
      const isLongString = value.length > 100;
      return (
        <span style={{
          color: '#86efac',
          wordBreak: isLongString ? 'break-all' : 'normal',
          display: 'block',
          whiteSpace: isLongString ? 'pre-wrap' : 'normal',
        }}>
          {value}
        </span>
      );
    }

    if (Array.isArray(value)) {
      return <span style={{ color: '#9ca3af', fontSize: '10px' }}>Array ({value.length} items)</span>;
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      return <span style={{ color: '#9ca3af', fontSize: '10px' }}>Object ({keys.length} properties)</span>;
    }

    return <span style={{ color: '#d1d5db' }}>{String(value)}</span>;
  };

  const renderSection = (obj: any, sectionTitle?: string, depth = 0): React.ReactNode => {
    if (!obj || typeof obj !== 'object') return null;

    // Sort: simple first, then complex
    const entries = Object.entries(obj).sort(([, valueA], [, valueB]) => {
      const isComplexA = typeof valueA === 'object' && valueA !== null;
      const isComplexB = typeof valueB === 'object' && valueB !== null;
      return isComplexA === isComplexB ? 0 : isComplexA ? 1 : -1;
    });

    const simpleFields: [string, any][] = [];
    const complexFields: [string, any][] = [];

    entries.forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        complexFields.push([key, value]);
      } else {
        simpleFields.push([key, value]);
      }
    });

    return (
      <div key={sectionTitle} style={{ marginBottom: depth === 0 ? '20px' : '16px' }}>
        {sectionTitle && depth > 0 && (
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#22c55e',
            marginBottom: '8px',
            paddingBottom: '6px',
            borderBottom: '1px solid rgba(34, 197, 94, 0.2)',
          }}>
            {sectionTitle}
          </div>
        )}

        {/* Simple fields in grid */}
        {simpleFields.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '12px 20px',
            marginBottom: complexFields.length > 0 ? '12px' : '0px',
          }}>
            {simpleFields.map(([key, value]) => (
              <div key={key} style={{ minHeight: '32px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{
                  fontSize: '10px',
                  fontWeight: '600',
                  color: '#9ca3af',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  {key}
                </div>
                <div style={{ fontSize: '12px', color: '#d1d5db' }}>
                  {renderValue(value)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Complex fields as subsections */}
        {complexFields.map(([key, value]) => (
          <div key={key} style={{
            marginBottom: '12px',
            padding: '12px',
            background: 'rgba(15, 23, 42, 0.5)',
            borderLeft: '3px solid #3b82f6',
            borderRadius: '4px',
          }}>
            {renderSection(value, key, depth + 1)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: '4px 0px' }}>
      {title && (
        <div style={{
          fontSize: '12px',
          fontWeight: '600',
          color: '#22c55e',
          marginBottom: '12px',
          paddingBottom: '8px',
          borderBottom: '1px solid rgba(34, 197, 94, 0.2)',
        }}>
          {title}
        </div>
      )}
      {renderSection(data)}
    </div>
  );
};

// Smart metadata display that avoids duplicating massive nested data
const MetadataField: React.FC<{ value: any }> = ({ value }) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!value) return null;

  // Extract execution result if it exists
  const executionResult = value?.executionResult;
  const outputBranches = executionResult?.outputBranches;
  const outputData = executionResult?.outputData;

  if (!executionResult) {
    return <WebJsonField value={value} />;
  }

  return (
    <div>
      {/* Summary of key fields */}
      <div style={{ fontSize: '12px', color: '#d1d5db', marginBottom: '8px' }}>
        {outputData?.status && (
          <div style={{ marginBottom: '4px' }}>
            <span style={{ color: '#9ca3af' }}>Status: </span>
            <span>{outputData.status}</span>
          </div>
        )}
        {executionResult?.durationMs !== undefined && (
          <div style={{ marginBottom: '4px' }}>
            <span style={{ color: '#9ca3af' }}>Duration: </span>
            <span>{formatMs(executionResult.durationMs)}</span>
          </div>
        )}
        {executionResult?.isSuccess !== undefined && (
          <div style={{ marginBottom: '4px' }}>
            <span style={{ color: '#9ca3af' }}>Success: </span>
            <span>{executionResult.isSuccess ? <><Icons.CheckCircle /> Yes</> : <><Icons.XCircle /> No</>}</span>
          </div>
        )}
        {executionResult?.errorMessage && (
          <div style={{ marginBottom: '4px', color: '#ef4444' }}>
            <span style={{ color: '#9ca3af' }}>Error: </span>
            <span>{executionResult.errorMessage}</span>
          </div>
        )}
      </div>

      {/* Expandable raw metadata for advanced inspection */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          color: '#3b82f6',
          cursor: 'pointer',
          fontSize: '11px',
          textDecoration: 'underline',
          marginBottom: '6px',
        }}
      >
        {expanded ? '▼ Hide' : '▶ Show'} raw metadata
      </button>

      {expanded && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>
          <WebJsonField value={value} />
        </div>
      )}
    </div>
  );
};

function showToast(title: string, message: string): void {
  // Check if browser supports Notification API
  if ('Notification' in window) {
    if (Notification.permission === 'granted') {
      new Notification(title, {
        body: message,
        icon: 'warning',
        tag: 'hil-alert',
      });
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          new Notification(title, {
            body: message,
            icon: 'warning',
            tag: 'hil-alert',
          });
        }
      });
    }
  } else {
    // Fallback to alert if Notification API not available
    alert(`${title}\n${message}`);
  }
}
